import {observer} from 'mobx-react-lite';
import {useStoreContext} from '@store/store-context';
import {MoodDiv} from '../my_checkins-style';
import {Helper} from '@ui/atoms/helper';
import {Image} from '@ui/atoms/image';
import {TextField} from '@ui/molecules/field/textfield';
import {CTA} from '@ui/atoms/typography';
import {IEmoji} from '@ui/assets/emojis';
import {ReactNode, useEffect, useMemo, useState} from 'react';
import {PulseEmojiOption, usePulseEmoji} from '../hooks/usePulseEmoji';
import {ItemLoader} from '@ui/organisms/item-loader';
import {getPulseScaleOption} from '../../shared/pulse-scale';
import {
  SubmissionBlock,
  SubmissionBlockContent,
  SubmissionBlockHeader,
  SubmissionBlockTitle,
  SubmissionTypeTag,
} from '../my_checkins-style';

export const MoodBox = observer(
  ({
    value,
    isReadOnly,
    handleFormValueChange,
    debounceUpdatePulse,
    updatePulse,
    isRequired = true,
    header,
    prompt,
    isLoadingCycleData,
  }: {
    value: {
      mood: string;
      feedback: string;
      id: string;
    };
    isReadOnly: boolean;
    isSubmitted?: boolean;
    isRequired?: boolean;

    header?: ReactNode;
    prompt?: string;
    handleFormValueChange?: (value: any, field: string) => void;
    debounceUpdatePulse?: (value: any) => void;
    updatePulse?: (value: any) => void;
    isLoadingCycleData?: boolean;
  }) => {
    const {
      checkinsStore: {
        workspaceCheckins,
        pulseValidation,
        updatePulseValidationError,
      },
    } = useStoreContext();

    const [mood, setMood] = useState<null | string>(value?.mood);
    const {emojiOptions} = usePulseEmoji();

    useEffect(() => {
      setMood(value?.mood);
    }, [value?.mood]);

    const selectedMoodKey = getPulseScaleOption(mood)?.key;

    const handleMood = (
      moodKey: PulseEmojiOption['key'],
      responseValue: PulseEmojiOption['responseValue'],
    ) => {
      if (!isReadOnly) {
        if (mood !== null && selectedMoodKey === moodKey) {
          setMood(null);
          handleFormValueChange &&
            handleFormValueChange({...value, mood: null}, 'pulse');
        } else {
          updatePulseValidationError(null);
          updatePulse &&
            updatePulse({
              pulse: {...value, mood: responseValue},
              pulseId: value.id,
            });
          setMood(responseValue);
          handleFormValueChange &&
            handleFormValueChange({...value, mood: responseValue}, 'pulse');
        }
      }
    };

    const moods = useMemo(
      () =>
        emojiOptions.map(({emoji, key, label, responseValue}) => ({
          emoji,
          key,
          responseValue,
          text: label,
        })),
      [emojiOptions],
    );

    return (
      <>
        <SubmissionBlock $completed={isRequired && !!mood}>
          <SubmissionBlockHeader>
            <SubmissionTypeTag $type="pulse">Pulse</SubmissionTypeTag>
            <SubmissionBlockTitle>
              {header || prompt || 'Checking in—how are things going for you?'}
              {isRequired && <span className="required"> *</span>}
            </SubmissionBlockTitle>
          </SubmissionBlockHeader>
          <SubmissionBlockContent>
            <MoodDiv
              activated={mood !== null}
              error={pulseValidation?.error}
              className="bg-white">
              <div className="contentDiv">
                {isLoadingCycleData ? (
                  <div style={{padding: '24px'}}>
                    <ItemLoader />
                  </div>
                ) : (
                  <div
                    className="inner-content"
                    role="radiogroup"
                    aria-label="Pulse response">
                    {moods.map(
                      (
                        mood__: {
                          emoji: IEmoji;
                          key: PulseEmojiOption['key'];
                          responseValue: PulseEmojiOption['responseValue'];
                          text: string;
                        },
                        index,
                      ) => (
                        <button
                          type="button"
                          key={index + mood__.text}
                          className="emoji"
                          role="radio"
                          aria-checked={
                            mood !== null && mood__.key === selectedMoodKey
                          }
                          disabled={isReadOnly}
                          onClick={() =>
                            handleMood(mood__.key, mood__.responseValue)
                          }>
                          <span
                            className="halo"
                            style={{
                              background:
                                mood !== null && mood__.key === selectedMoodKey
                                  ? '#585ADF'
                                  : undefined,
                            }}>
                            <span className="img">
                              <Image {...mood__.emoji} />
                            </span>
                          </span>
                          <CTA kind="textMedium">{mood__.text}</CTA>
                        </button>
                      ),
                    )}
                  </div>
                )}
                {mood && !isLoadingCycleData && (
                  <div className="second-inner-content">
                    <TextField
                      optional
                      placeholder=""
                      label="Want to give more context?"
                      value={value.feedback ?? ''}
                      onChange={(e) => {
                        debounceUpdatePulse &&
                          debounceUpdatePulse({
                            pulse: {
                              ...value,
                              feedback: e?.target?.value,
                            },
                            pulseId: value.id,
                          });

                        handleFormValueChange &&
                          handleFormValueChange(
                            {...value, feedback: e?.target?.value},
                            'pulse',
                          );
                      }}
                      name="pulse.feedback"
                      key="feedback"
                      type="text"
                      readOnly={isReadOnly}
                      disabled={workspaceCheckins?.isReviewed || isReadOnly}
                    />
                  </div>
                )}
              </div>
            </MoodDiv>
          </SubmissionBlockContent>
        </SubmissionBlock>
        <Helper
          aria-label="helper"
          state={pulseValidation?.error ? 'error' : 'default'}
          children={pulseValidation?.error || ''}
        />
      </>
    );
  },
);
